/**
 * CompanyRegistration Component
 * Register and verify company for employers
 * Requires Business Plus subscription
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockIcon from '@mui/icons-material/Lock';
import { jobsService, Company } from '@services/jobsService';
import { subscriptionService } from '@services/subscriptionService';
import { telegram } from '@services/telegram';
import { useAutoDismiss } from '../../hooks/useAutoDismiss';

interface CompanyRegistrationProps {
  open?: boolean;
  onClose?: () => void;
  onRegistered?: () => void;
  onVerified?: () => void;
  company?: Company | null;
  onUpgradeClick?: () => void;
}

export function CompanyRegistration({ 
  open = true, 
  onClose, 
  onRegistered, 
  onVerified,
  company,
  onUpgradeClick
}: CompanyRegistrationProps) {
  const [step, setStep] = useState<'register' | 'verify'>(company?.status === 'pending' ? 'verify' : 'register');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isBusinessPlus, setIsBusinessPlus] = useState<boolean | null>(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);

  useAutoDismiss(error, () => setError(null));

  // Check Business Plus subscription on mount
  useEffect(() => {
    const checkSubscription = async () => {
      setCheckingSubscription(true);
      try {
        const bp = await subscriptionService.isBusinessPlus();
        setIsBusinessPlus(bp);
      } catch (err) {
        console.error('Failed to check subscription:', err);
        setIsBusinessPlus(false);
      }
      setCheckingSubscription(false);
    };
    checkSubscription();
  }, []);

  // Registration form
  const [companyName, setCompanyName] = useState(company?.name || '');
  const [email, setEmail] = useState(company?.email || '');
  const [website, setWebsite] = useState(company?.website || '');
  const [description, setDescription] = useState(company?.description || '');
  const [phone, setPhone] = useState(company?.phone || '');

  // Verification form
  const [verificationCode, setVerificationCode] = useState('');
  const [resending, setResending] = useState(false);

  const handleRegister = async () => {
    if (!companyName.trim() || !email.trim()) {
      setError('Company name and business email are required');
      return;
    }

    // Basic email validation
    if (!email.includes('@') || email.includes('gmail.com') || email.includes('yahoo.com') || 
        email.includes('hotmail.com') || email.includes('outlook.com')) {
      setError('Please use a business email address, not a personal email');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await jobsService.registerCompany({
        companyName,
        email,
        website,
        description,
        phone
      });

      if (result.success) {
        setSuccess('Company registered! Check your email for the verification code.');
        setStep('verify');
        telegram.hapticFeedback('success');
        if (onRegistered) onRegistered();
      } else {
        setError(result.error || 'Failed to register company');
        telegram.hapticFeedback('error');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to register company');
      telegram.hapticFeedback('error');
    }

    setLoading(false);
  };

  const handleVerify = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await jobsService.verifyCompany(verificationCode);

      if (result.success) {
        setSuccess('Company verified! You can now post jobs.');
        telegram.hapticFeedback('success');
        if (onVerified) onVerified();
        if (onClose) setTimeout(onClose, 1500);
      } else {
        setError(result.error || 'Invalid verification code');
        telegram.hapticFeedback('error');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to verify company');
      telegram.hapticFeedback('error');
    }

    setLoading(false);
  };

  const handleResendCode = async () => {
    setResending(true);
    setError(null);

    try {
      const result = await jobsService.resendVerificationCode();

      if (result.success) {
        setSuccess('New verification code sent!');
        telegram.hapticFeedback('success');
      } else {
        setError(result.error || 'Failed to resend code');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to resend code');
    }

    setResending(false);
  };

  const renderRegistrationForm = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="info" sx={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
        Register your company using a business email address. We'll send a verification code to confirm ownership.
      </Alert>

      <TextField
        fullWidth
        size="small"
        label="Company Name"
        value={companyName}
        onChange={(e) => setCompanyName(e.target.value)}
        required
        sx={textFieldSx}
      />

      <TextField
        fullWidth
        size="small"
        label="Business Email"
        type="email"
        placeholder="you@yourcompany.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        helperText="Must be a business email (not Gmail, Yahoo, etc.)"
        sx={textFieldSx}
      />

      <TextField
        fullWidth
        size="small"
        label="Website (optional)"
        placeholder="https://yourcompany.com"
        value={website}
        onChange={(e) => setWebsite(e.target.value)}
        sx={textFieldSx}
      />

      <TextField
        fullWidth
        size="small"
        label="Phone (optional)"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        sx={textFieldSx}
      />

      <TextField
        fullWidth
        size="small"
        label="Company Description (optional)"
        multiline
        rows={2}
        placeholder="Tell job seekers about your company..."
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        sx={textFieldSx}
      />

      <Button
        fullWidth
        variant="contained"
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <BusinessIcon />}
        onClick={handleRegister}
        disabled={loading}
        sx={{ 
          mt: 1,
          backgroundColor: '#3b82f6',
          '&:hover': { backgroundColor: '#2563eb' }
        }}
      >
        {loading ? 'Registering...' : 'Register Company'}
      </Button>
    </Box>
  );

  const renderVerificationForm = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Alert severity="info" sx={{ backgroundColor: 'rgba(59, 130, 246, 0.1)' }}>
        <Typography variant="body2">
          We sent a 6-digit verification code to <strong>{email || company?.email}</strong>
        </Typography>
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.8 }}>
          Check your inbox and spam folder
        </Typography>
      </Alert>

      <Box sx={{ textAlign: 'center', my: 2 }}>
        <EmailIcon sx={{ fontSize: 48, color: '#3b82f6', mb: 1 }} />
        <Typography variant="body1" sx={{ color: 'white' }}>
          Enter Verification Code
        </Typography>
      </Box>

      <TextField
        fullWidth
        size="small"
        label="6-Digit Code"
        value={verificationCode}
        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="000000"
        inputProps={{ 
          maxLength: 6,
          style: { 
            textAlign: 'center', 
            fontSize: '24px', 
            letterSpacing: '8px',
            fontFamily: 'monospace'
          }
        }}
        sx={textFieldSx}
      />

      <Button
        fullWidth
        variant="contained"
        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
        onClick={handleVerify}
        disabled={loading || verificationCode.length !== 6}
        sx={{ 
          backgroundColor: '#22c55e',
          '&:hover': { backgroundColor: '#16a34a' }
        }}
      >
        {loading ? 'Verifying...' : 'Verify Company'}
      </Button>

      <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 1 }} />

      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.6)', mb: 1 }}>
          Didn't receive the code?
        </Typography>
        <Button
          size="small"
          startIcon={resending ? <CircularProgress size={16} /> : <RefreshIcon />}
          onClick={handleResendCode}
          disabled={resending}
          sx={{ color: '#3b82f6' }}
        >
          {resending ? 'Sending...' : 'Resend Code'}
        </Button>
      </Box>
    </Box>
  );

  // Business Plus required content
  const renderBusinessPlusRequired = () => (
    <Box sx={{ textAlign: 'center', py: 4 }}>
      <LockIcon sx={{ fontSize: 64, color: '#f59e0b', mb: 2 }} />
      <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
        Business Plus Required
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 3 }}>
        Company registration is available exclusively for Business Plus subscribers.
        Upgrade to register your company, post job listings, and access the bidding system.
      </Typography>
      <Button
        variant="contained"
        onClick={onUpgradeClick}
        sx={{
          backgroundColor: '#f59e0b',
          color: 'black',
          fontWeight: 600,
          '&:hover': { backgroundColor: '#d97706' }
        }}
      >
        Upgrade to Business Plus
      </Button>
      <Typography variant="caption" sx={{ display: 'block', mt: 2, color: 'rgba(255,255,255,0.5)' }}>
        $199.99/year • 30% goes to ZiaGivesBack charity
      </Typography>
    </Box>
  );

  // If used inline (not as dialog)
  if (!onClose) {
    if (checkingSubscription) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      );
    }
    
    if (!isBusinessPlus) {
      return renderBusinessPlusRequired();
    }

    return (
      <Box>
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        {step === 'register' ? renderRegistrationForm() : renderVerificationForm()}
      </Box>
    );
  }

  // Dialog version
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1e293b',
          border: '1px solid rgba(59, 130, 246, 0.3)',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ 
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BusinessIcon sx={{ color: '#3b82f6' }} />
          <Typography variant="h6">
            {!isBusinessPlus ? 'Business Plus Required' : (step === 'register' ? 'Register Company' : 'Verify Company')}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {checkingSubscription ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : !isBusinessPlus ? (
          renderBusinessPlusRequired()
        ) : (
          <>
            {error && (
              <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}
            
            {step === 'register' ? renderRegistrationForm() : renderVerificationForm()}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Common styles
const textFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'rgba(255,255,255,0.05)',
    '& input, & textarea': { color: 'white' },
    '& fieldset': { borderColor: 'rgba(255,255,255,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
    '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
  '& .MuiFormHelperText-root': { color: 'rgba(255,255,255,0.5)' }
};

export default CompanyRegistration;
